import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { MapPin, ChevronRight, Mail, Phone } from 'lucide-react';
import Navbar from '@/components/landing/Navbar';
import Footer from '@/components/landing/Footer';
import FinalCTA from '@/components/landing/FinalCTA';
import SEO from '@/components/SEO';
import {
  SUPPORT_EMAIL,
  SUPPORT_EMAIL_MAILTO,
  SUPPORT_PHONE_DUMMY_DISPLAY,
  SUPPORT_PHONE_DUMMY_TEL
} from '@/config/links';

const checkerSteps = [
  {
    step: 1,
    title: 'Visit Boxd Logistics',
    body: 'Sign in to your boxd logistics dashboard.'
  },
  {
    step: 2,
    title: 'Open the checker',
    body: 'Go to the Serviceability section in your account.'
  },
  {
    step: 3,
    title: 'Enter your pin code',
    body: 'Type your 6-digit pin code or postal code in the designated field.'
  },
  {
    step: 4,
    title: 'Verify',
    body: 'Click Check or Verify to submit your pin code.'
  },
  {
    step: 5,
    title: 'See instant results',
    body: 'Our system shows whether your location is in our serviceable areas so you can plan shipping with confidence.'
  }
];

const whyUse = [
  {
    title: 'Convenience',
    body: 'Quickly see if our shipping services cover your location—no long research or back-and-forth.'
  },
  {
    title: 'Efficiency',
    body: 'Check serviceability before you ship so you can plan fulfilment and set the right expectations with customers.'
  },
  {
    title: 'Accuracy',
    body: 'Our serviceable list is updated regularly to reflect where we can deliver today.'
  }
];

const ServiceablePincode = () => {
  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Check Serviceable Pincodes"
        description="Instantly check serviceable pincodes across India for your eCommerce shipments with Boxd Logistics."
        keywords="serviceable pincode, delivery pincodes india, check pincode, cod serviceability"
        canonical="https://boxdlogistics.in/serviceable-pincode"
      />
      <Navbar />

      {/* Hero — homepage-style gradient + grid */}
      <section className="relative pt-28 pb-12 md:pt-32 md:pb-16 px-4 bg-gradient-to-br from-blue-50 via-white to-blue-50 overflow-hidden">
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage:
              'linear-gradient(rgba(59, 130, 246, 0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(59, 130, 246, 0.03) 1px, transparent 1px)',
            backgroundSize: '50px 50px'
          }}
        />
        <div className="absolute top-20 right-10 w-72 h-72 bg-gradient-to-br from-blue-200/25 to-blue-300/10 rounded-full blur-3xl" />
        <div className="absolute bottom-10 left-10 w-64 h-64 bg-gradient-to-br from-blue-100/30 to-transparent rounded-full blur-3xl" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex items-center gap-2 text-sm text-gray-500 mb-8">
            <Link to="/" className="hover:text-blue-800 transition-colors">
              Home
            </Link>
            <ChevronRight className="w-4 h-4 shrink-0" />
            <span className="text-gray-900 font-medium">Serviceable Pin Codes</span>
          </nav>

          <div className="max-w-4xl">
            <div className="inline-flex items-center justify-center w-16 h-16 md:w-20 md:h-20 bg-gradient-to-br from-blue-700 to-blue-800 rounded-2xl mb-6 shadow-lg shadow-blue-700/20">
              <MapPin className="w-8 h-8 md:w-10 md:h-10 text-white" />
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-4 leading-tight">
              Serviceable{' '}
              <span className="bg-gradient-to-r from-blue-800 to-blue-950 bg-clip-text text-transparent">
                Pin Codes
              </span>
            </h1>
            <h2 className="text-xl md:text-2xl font-semibold text-gray-800 mb-6">
              Check if your pin code is serviceable
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed max-w-3xl">
              At Boxd Logistics, we are dedicated to providing reliable shipping to as many locations as possible. We
              maintain a list of serviceable pin codes where our services are available. If you are wondering whether we
              can ship to your area, you are in the right place.
            </p>
          </div>
        </div>
      </section>

      {/* How to use — HowItWorks-style cards */}
      <section className="relative py-20 px-4 bg-gradient-to-br from-gray-50 via-white to-blue-50 overflow-hidden">
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage:
              'linear-gradient(rgba(59, 130, 246, 0.02) 2px, transparent 2px), linear-gradient(90deg, rgba(59, 130, 246, 0.02) 2px, transparent 2px)',
            backgroundSize: '60px 60px'
          }}
        />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <span className="text-blue-800 font-semibold text-sm uppercase tracking-wide">How it works</span>
            <h2 className="text-3xl md:text-4xl font-bold mt-2 mb-4 text-gray-900">
              Using our <span className="text-blue-800">serviceable pin codes</span> checker
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto text-lg">
              Follow these steps to verify whether your pin code is included in our serviceable areas.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {checkerSteps.map((item) => (
              <div
                key={item.step}
                className="relative bg-white p-8 rounded-2xl border-2 border-gray-200 hover:border-blue-300 shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <div className="absolute -top-3 -right-3 w-10 h-10 bg-gradient-to-br from-blue-700 to-blue-800 rounded-full flex items-center justify-center text-white font-bold text-sm shadow-md">
                  {item.step}
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3 pr-8">{item.title}</h3>
                <p className="text-gray-600 leading-relaxed">{item.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why use + expansion — content from live site */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Why use the <span className="text-blue-800">pin code checker?</span>
              </h2>
              <ul className="space-y-6">
                {whyUse.map((item, i) => (
                  <li key={i} className="flex gap-4">
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-blue-100 text-blue-900 font-bold text-sm">
                      {i + 1}
                    </span>
                    <div>
                      <h3 className="font-bold text-gray-900 mb-1">{item.title}</h3>
                      <p className="text-gray-600 leading-relaxed">{item.body}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-2xl border-2 border-gray-200 bg-gradient-to-br from-blue-50/50 to-white p-8 md:p-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Serviceable areas expansion</h2>
              <p className="text-gray-600 leading-relaxed mb-6">
                We continuously work to expand serviceable areas and reach more customers. If your pin code is not
                serviceable today, check back regularly—we may extend coverage to your area in the future.
              </p>
              <p className="text-gray-600 leading-relaxed">
                If your pin code is serviceable, you can ship with Boxd Logistics with confidence. For including any
                non-serviceable pin codes, you can reach out to{' '}
                <a href={SUPPORT_EMAIL_MAILTO} className="font-semibold text-blue-900 hover:text-blue-950">
                  {SUPPORT_EMAIL}
                </a>
                .
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact — aligned with footer contact info */}
      <section className="py-16 px-4 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white">
        <div className="max-w-4xl mx-auto text-center px-4">
          <h2 className="text-2xl md:text-3xl font-bold mb-4">Contact us for assistance</h2>
          <p className="text-gray-300 leading-relaxed mb-8">
            If you have questions about serviceable pin codes or need guidance on shipping options, our team is here to
            help.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-6 text-sm md:text-base">
            <a
              href={SUPPORT_EMAIL_MAILTO}
              className="inline-flex items-center gap-2 text-blue-300 hover:text-blue-200 font-medium"
            >
              <Mail className="w-5 h-5" />
              {SUPPORT_EMAIL}
            </a>
            <span className="hidden sm:inline text-gray-600">|</span>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-6 text-gray-200">
              <a href={`tel:${SUPPORT_PHONE_DUMMY_TEL}`} className="inline-flex items-center gap-2 hover:text-blue-300">
                <Phone className="w-4 h-4" />
                {SUPPORT_PHONE_DUMMY_DISPLAY}
              </a>
            </div>
          </div>
          <div className="mt-8">
            <Button
              asChild
              variant="outline"
              className="border-2 border-white/40 text-white hover:bg-white/10 rounded-xl"
            >
              <Link to="/contact">Go to contact page</Link>
            </Button>
          </div>
        </div>
      </section>

      <p className="text-center text-gray-500 text-sm py-6 px-4 bg-white">
        Thank you for considering Boxd Logistics. We look forward to serving you and expanding our reach to more pin
        codes.
      </p>

      <FinalCTA />
      <Footer />
    </div>
  );
};

export default ServiceablePincode;
